import React, { useState, useEffect, ReactNode, useCallback } from 'react'
import { activateLocale, SupportedLocale, defaultLocale, supportedLocales, i18n } from '../i18n'
import { LanguageContext, LanguageContextType } from './LanguageContextDefinition'

interface LanguageProviderProps {
  children: ReactNode
}

const LOCALE_STORAGE_KEY = 'aiquitas-locale'

/**
 * Detect browser language preference
 */
function detectBrowserLanguage(): SupportedLocale {
  if (typeof window === 'undefined') return defaultLocale
  
  const browserLang = navigator.language.split('-')[0] as SupportedLocale
  return supportedLocales.includes(browserLang) ? browserLang : defaultLocale
}

/**
 * Get stored locale from localStorage or detect from browser
 */
function getInitialLocale(): SupportedLocale {
  if (typeof window === 'undefined') return defaultLocale
  
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY) as SupportedLocale
  if (stored && supportedLocales.includes(stored)) {
    return stored
  }
  
  return detectBrowserLanguage()
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [locale, setLocaleState] = useState<SupportedLocale>(getInitialLocale)
  const [isLoading, setIsLoading] = useState(false)
  // Force re-render key when locale changes
  const [renderKey, setRenderKey] = useState(0)

  const setLocale = useCallback(async (newLocale: SupportedLocale) => {
    if (newLocale === locale) return
    
    setIsLoading(true)
    
    try {
      // Activate the new locale in i18n
      await activateLocale(newLocale)
      
      // Update state
      setLocaleState(newLocale)
      
      // Persist to localStorage
      localStorage.setItem(LOCALE_STORAGE_KEY, newLocale)
      
      // Update document language attribute
      document.documentElement.lang = newLocale
      
      // Force re-render of all children
      setRenderKey(prev => prev + 1)
    } catch (error) {
      console.error('Failed to switch language:', error)
    } finally {
      setIsLoading(false)
    }
  }, [locale])

  // Initialize i18n on mount with stored/detected locale
  useEffect(() => {
    const initializeLanguage = async () => {
      const initialLocale = getInitialLocale()
      
      setIsLoading(true)
      try {
        // Always activate the initial locale to ensure messages are loaded
        await activateLocale(initialLocale)
        setLocaleState(initialLocale)
        document.documentElement.lang = initialLocale
      } catch (error) {
        console.error('Failed to initialize language:', error)
        // Fallback to default locale
        document.documentElement.lang = defaultLocale
      } finally {
        setIsLoading(false)
      }
    }

    initializeLanguage()
  }, [])

  const value: LanguageContextType = {
    locale,
    setLocale,
    isLoading
  }

  return (
    <LanguageContext.Provider value={value} key={renderKey}>
      {children}
    </LanguageContext.Provider>
  )
}